import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EXPLICACAO, faltaPraCotaZerar } from "@nhomi/core";
import { usePlano } from "@/lib/plano";
import { detalhes, disponivel, podeComprar, type DetalhesDoItem } from "@/lib/billing";
import { sistema } from "@/lib/plataforma";
import { assinar, type Confirmacao } from "@/data/assinatura";
import { Spinner, TopBar } from "@/ui/primitives";
import { IconCheck } from "@/ui/icons";

/* ============================================================
   Assinar

   A tela que faltava. O app inteiro já dizia "ou assine" em quatro
   lugares — na Home sem vaga, ao criar desafio, ao entrar por convite e
   ao acabar a cota de caloria — e nenhum deles levava a lugar nenhum.

   O QUE ELA VENDE, e o que ela não vende: nada que mexa no placar.
   Assinar tira anúncio, solta o limite de desafios ao mesmo tempo e
   abre a cota de estimativa de caloria. Não compra ponto, não compra
   vantagem, não compra visibilidade. Num app onde amigos competem,
   ganhar porque pagou mata o placar — e essa decisão é de agosto, está
   registrada, e esta tela é o lugar onde seria mais fácil de trair.

   TRÊS ESTADOS, e o do meio é o que costuma faltar:

     - já assina → não tem o que vender, tem o que explicar;
     - dá pra comprar aqui → a oferta, com o preço vindo da loja;
     - NÃO dá pra comprar aqui → navegador comum, fora do app. Nem
       esconder a tela nem mostrar um botão que morre calado: dizer o que
       é, que é "isso se faz no app do Android".
   ============================================================ */

export function Assinar() {
  const { assinante, config } = usePlano();
  const navigate = useNavigate();

  /* POR QUE a pessoa chegou aqui.

     Quem encosta no teto de caloria e toca em "soltar o limite" chega
     com um assunto na cabeça. Abrir a mesma vitrine de sempre faz a tela
     parecer propaganda que apareceu do nada; abrir respondendo o que
     acabou de acontecer faz dela a continuação da frase anterior.

     As outras portas pro Pro (sem vaga de desafio, criar, convite) não
     mandam motivo nenhum e caem no caso de sempre — o `?.` já cobre. */
  const location = useLocation();
  const veioDaCota =
    (location.state as { porque?: string } | null)?.porque === "cota-de-caloria";

  const [temLoja, setTemLoja] = useState<boolean | null>(null);
  const [vende, setVende] = useState(false);
  const [item, setItem] = useState<DetalhesDoItem | null>(null);
  const [indo, setIndo] = useState(false);
  const [recado, setRecado] = useState("");
  const [pronto, setPronto] = useState<Confirmacao | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const ok = await disponivel();
      if (!vivo) return;
      setTemLoja(ok);
      if (!ok) return;

      /* As duas perguntas em paralelo, e é a SEGUNDA que manda.
         Consultar e comprar são caminhos Android separados: o preço vem
         pela Digital Goods API, a compra vai pela PaymentActivity. Já
         houve aparelho com a primeira quebrada e a segunda funcionando —
         e a tela recusava a venda por não ter conseguido ler o preço,
         que é enfeite perto de poder cobrar. */
      const [achado, vendeAqui] = await Promise.all([detalhes(), podeComprar()]);
      if (!vivo) return;
      setItem(achado);
      setVende(vendeAqui);
    })();
    return () => { vivo = false; };
  }, []);

  async function comprarAgora() {
    setIndo(true);
    setRecado("");
    const r = await assinar();
    setIndo(false);

    if (r.ok) { setPronto(r.confirmacao); return; }

    /* Desistir não é erro. Quem olhou o preço e fechou a folha não fez
       nada errado, e um aviso vermelho aqui trataria o cliente como
       problema. */
    if (r.motivo === "cancelada") return;
    setRecado(
      r.motivo === "indisponivel"
        ? "A Play Store não abriu a cobrança neste aparelho. Confira se você "
          + "está com uma conta Google com forma de pagamento."
        : r.detalhe || "Não deu pra concluir agora. Tente de novo em instantes.",
    );
  }

  /* ---------- acabou de assinar ---------- */

  if (pronto?.assinante) {
    return (
      <main className="screen">
        <TopBar title="Assinatura" back="/" />
        <div className="screen-body pad">
          <div className="assinar-feito">
            <div className="assinar-selo">★</div>
            <h2>Pronto, você assinou</h2>
            <p>{EXPLICACAO[pronto.estado]}</p>
            <p className="fine">
              A cobrança e o cancelamento ficam na Play Store, em Pagamentos
              e assinaturas. A gente não guarda nada do seu cartão.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate("/", { replace: true })}>
            Voltar aos desafios
          </button>
        </div>
      </main>
    );
  }

  /* ---------- já era assinante ---------- */

  if (assinante) {
    return (
      <main className="screen">
        <TopBar title="Assinatura" back="/" />
        <div className="screen-body pad">
          <div className="assinar-feito">
            <div className="assinar-selo">★</div>
            <h2>Você já assina</h2>
            <p>Sem anúncio, desafios à vontade e calorias sem racionar.</p>
          </div>
          {/* Cancelar é na loja, e a loja exige que a gente diga onde.
              Esconder isso é o tipo de coisa que vira nota um. */}
          <div className="hint-row">
            Para trocar a forma de pagamento ou cancelar, vá na Play Store →
            Pagamentos e assinaturas. Ao cancelar, o acesso continua até o
            fim do período que você já pagou.
          </div>
          <Link className="btn btn-outline" to="/conta">Minha conta</Link>
        </div>
      </main>
    );
  }

  if (temLoja === null) {
    return (
      <main className="screen">
        <TopBar title="Assinar" back="/" />
        <Spinner />
      </main>
    );
  }

  /* ---------- a oferta ---------- */

  return (
    <main className="screen">
      <TopBar title="Assinar" back="/" />
      <div className="screen-body pad">

        {veioDaCota && !assinante && (
          <div className="assinar-porque">
            <div className="assinar-porque-t">🔥 Suas estimativas de hoje acabaram</div>
            <div className="assinar-porque-s">
              O grátis dá {config.tetoCaloriaGratis} por dia e zera à
              meia-noite — daqui a <b>{faltaPraCotaZerar()}</b>. Dá pra
              esperar; o Pro é pra quem não quer contar.
            </div>
          </div>
        )}

        <div className="assinar-topo">
          <h2>Nhomi Pro</h2>
          <p className="assinar-sub">
            O plano grátis dá conta de quase tudo. Isto aqui é pra quem
            está em desafio o ano inteiro.
          </p>
        </div>

        {/* Cada item com o seu ✓: sem marcador, dois itens de duas linhas
            viram um parágrafo só e ninguém conta três coisas. O ✓ é o
            que faz a lista ser escaneável em vez de lida. */}
        {/* SEM NÚMERO do lado do Pro, de propósito. "100 no lugar de 5"
            descreve o que o Pro NÃO faz — e um teto escrito na vitrine
            faz o plano pago parecer racionado também. O teto existe
            (está em `config/app`) e é alto o bastante pra nunca ser
            encostado por gente; ele é freio contra laço com bug, não
            régua de produto.

            O número do GRÁTIS fica, e vem do banco: é ele que a pessoa
            está encostando agora, e é o que dá sentido à oferta. */}
        <ul className="assinar-lista">
          <li>
            <IconCheck size={18} />
            <span>
              <strong>Desafios à vontade</strong> — entre em quantos quiser.
              O grátis vai até {config.desafiosGratis} ao mesmo tempo.
            </span>
          </li>
          <li>
            <IconCheck size={18} />
            <span><strong>Sem anúncio</strong> — some da Home, do feed e do prato.</span>
          </li>
          <li>
            <IconCheck size={18} />
            <span>
              <strong>Calorias à vontade</strong> — peça a estimativa quantas
              vezes precisar. O grátis dá {config.tetoCaloriaGratis} por dia.
            </span>
          </li>
        </ul>

        {temLoja && vende ? (
          <>
            {/* Preço e botão num bloco só. Soltos, o preço virava um
                número boiando entre dois textos e a pessoa tocava em
                "Assinar" sem ter certeza de quanto ia pagar.

                Quando o preço não vem, o botão FICA. A folha da própria
                Play mostra o valor antes de cobrar qualquer coisa — o
                Chrome inclusive avisa que o total que a gente manda é
                ignorado e que vale o do produto cadastrado. Esconder a
                venda por causa do enfeite seria perder a venda. */}
            <div className="assinar-fecho">
              {item ? (
                <div className="assinar-preco">
                  <span className="valor">{formatarPreco(item)}</span>
                  <span className="periodo">{periodo(item.subscriptionPeriod)}</span>
                </div>
              ) : (
                <div className="assinar-semvalor">
                  O valor aparece na tela da Play Store, antes de confirmar.
                </div>
              )}

              {recado && <div className="hint-row erro">{recado}</div>}

              <button className="btn btn-primary" disabled={indo} onClick={comprarAgora}>
                {indo ? "Abrindo a Play Store…" : "Assinar"}
              </button>
            </div>

            {/* O que NÃO vem junto, logo abaixo do botão. Antes ele ficava
                entre a lista e o preço e cortava o caminho oferta → preço
                → ação bem no meio. Continua acima da letra miúda: é
                promessa de produto, não termo de serviço. */}
            <div className="assinar-nao">
              Nenhum ponto, nenhuma vantagem no placar. Isso não está à venda —
              o jogo é entre vocês.
            </div>

            <p className="fine">
              A cobrança é da Google Play, na sua conta Google. Renova sozinha
              e você cancela quando quiser, na própria Play Store. Ao assinar
              você concorda com a <Link to="/privacidade">política de privacidade</Link>.
            </p>
          </>
        ) : temLoja ? (
          /* A loja abriu mas não devolveu o produto. Quase sempre é
             cadastro no Play Console ainda não ativo, ou pacote de teste
             sem o produto liberado — e isso não é culpa de quem está
             olhando a tela. */
          <div className="cota-acabou">
            <div className="cota-titulo">A assinatura não está disponível agora</div>
            <div className="cota-sub">
              A gente não conseguiu carregar o plano da loja. Tente daqui a
              pouco — nada foi cobrado.
            </div>
          </div>
        ) : (
          /* Nem esconder, nem botão que morre calado: a cobrança passa
             pela Play e aqui NÃO tem como cobrar. Mas "por aqui não dá"
             quer dizer coisas MUITO diferentes dependendo de onde a
             pessoa está, e a mesma frase para as três era quase mentira
             no iPhone: mandava instalar um app que não existe. */
          <SemVenda />
        )}

        <div className="gap" />
      </div>
    </main>
  );
}

/**
 * O que dizer quando não dá pra cobrar AQUI.
 *
 * Três situações que a mesma frase tratava igual, e uma delas era quase
 * mentira: no iPhone o texto mandava instalar pela Play Store, ou seja,
 * prometia um caminho que não existe. Quem está no iPhone não tem o que
 * fazer hoje — e merece ouvir isso, não uma instrução impossível.
 */
function SemVenda() {
  const onde = sistema();

  if (onde === "ios") {
    return (
      <div className="cota-acabou">
        <div className="cota-titulo">Ainda não dá pra assinar no iPhone</div>
        <div className="cota-sub">
          O Nhomi ainda não está na App Store, e a assinatura passa pela
          loja do aparelho. Estamos trabalhando nisso — quando o app sair,
          o Pro vem junto, e a sua conta continua a mesma.
        </div>
      </div>
    );
  }

  if (onde === "computador") {
    return (
      <div className="cota-acabou">
        <div className="cota-titulo">A assinatura é pelo celular</div>
        <div className="cota-sub">
          A cobrança passa pela Google Play, que só existe no Android.
          Abra o Nhomi no celular com esta mesma conta — tudo que é seu já
          está lá.
        </div>
      </div>
    );
  }

  /* Android fora do app instalado: aqui o caminho EXISTE, e é curto. */
  return (
    <div className="cota-acabou">
      <div className="cota-titulo">Por aqui não dá pra assinar</div>
      <div className="cota-sub">
        A assinatura passa pela Google Play, então ela só aparece no app
        instalado. Baixe o Nhomi pela Play Store e entre com esta mesma
        conta — tudo que é seu já está lá.
      </div>
    </div>
  );
}

/**
 * O preço, do jeito que a loja mandou.
 *
 * Vem em unidades da moeda como string ("4.90") mais o código ISO. A
 * formatação é nossa, mas o NÚMERO e a MOEDA são da Play — ela já
 * resolveu conversão, imposto e promoção. Não recalcule nada aqui.
 */
function formatarPreco(item: DetalhesDoItem): string {
  const valor = Number(item.price?.value);
  const moeda = item.price?.currency || "BRL";
  if (!Number.isFinite(valor)) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: moeda })
    .format(valor);
}

/** "P1M" e "P1Y" são o que a Play manda; ninguém lê ISO 8601 numa tela. */
function periodo(iso: string | undefined): string {
  if (iso === "P1Y") return "por ano";
  if (iso === "P1W") return "por semana";
  if (iso === "P1M" || !iso) return "por mês";
  return "por período";
}
